"use client";

import { useEffect, useState } from "react";
import AlbumArt from "./components/AlbumArt";
import NowPlaying from "./components/NowPlaying";
import StreamPlayer from "./components/StreamPlayer";
import ListenerCount from "./components/ListenerCount";
import SearchBar from "./components/SearchBar";
import Queue, { type QueueTrack } from "./components/Queue";
import OnboardingModal from "./components/OnboardingModal";
import { getNowPlaying, getStatus, getQueue, getMode, type SearchTrack } from "@/lib/api";

interface OptimisticTrack extends QueueTrack {
  uri: string;
  addedAt: number;
}

const OPTIMISTIC_TTL_MS = 3 * 60 * 1000; // drop optimistic items after 3 min as a safety net

export default function Home() {
  const [np, setNp] = useState({
    playing: false,
    artist: "",
    track: "",
    album_art: "",
    progress_ms: 0,
    duration_ms: 0,
  });
  const [listeners, setListeners] = useState(0);
  const [queue, setQueue] = useState<QueueTrack[]>([]);
  const [optimistic, setOptimistic] = useState<OptimisticTrack[]>([]);
  const [mode, setModeState] = useState("jukebox");

  useEffect(() => {
    getMode().then((r) => setModeState(r.mode)).catch(() => {});
    const modeInterval = setInterval(() => {
      getMode().then((r) => setModeState(r.mode)).catch(() => {});
    }, 30000);
    return () => clearInterval(modeInterval);
  }, []);

  useEffect(() => {
    getNowPlaying().then(setNp).catch(() => {});
    const npInterval = setInterval(() => {
      getNowPlaying().then(setNp).catch(() => {});
    }, 30000);
    return () => clearInterval(npInterval);
  }, []);

  useEffect(() => {
    getStatus().then((s) => setListeners(s.listeners)).catch(() => {});
    const statusInterval = setInterval(() => {
      getStatus().then((s) => setListeners(s.listeners)).catch(() => {});
    }, 30000);
    return () => clearInterval(statusInterval);
  }, []);

  function refreshQueue() {
    getQueue()
      .then((d) => {
        setQueue(d.queue);
        const realUris = new Set(d.queue.map((t) => t.uri).filter(Boolean) as string[]);
        const now = Date.now();
        setOptimistic((prev) =>
          prev.filter(
            (o) => !realUris.has(o.uri) && now - o.addedAt < OPTIMISTIC_TTL_MS,
          ),
        );
      })
      .catch(() => {});
  }

  useEffect(() => {
    refreshQueue();
    const id = setInterval(refreshQueue, 20000);
    return () => clearInterval(id);
  }, []);

  function handleQueued(track: SearchTrack) {
    setOptimistic((prev) => {
      if (prev.some((o) => o.uri === track.uri)) return prev;
      return [
        ...prev,
        {
          uri: track.uri,
          title: track.title,
          artist: track.artist,
          album_art: track.album_art,
          addedAt: Date.now(),
          pending: true,
        },
      ];
    });
    // Spotify takes a beat to surface the new queue item — refetch shortly after.
    setTimeout(refreshQueue, 4000);
  }

  function handleQueueFailed(uri: string) {
    setOptimistic((prev) => prev.filter((o) => o.uri !== uri));
  }

  const merged: QueueTrack[] = [
    ...optimistic.map((o) => ({ ...o, pending: true })),
    ...queue.filter((q) => !optimistic.some((o) => o.uri === q.uri)),
  ];

  return (
    <div className="min-h-screen flex flex-col items-center px-4 py-8 max-w-md mx-auto gap-6">
      <OnboardingModal />
      <header className="flex items-center justify-between w-full">
        <h1 className="text-xl font-bold tracking-tight">
          <span style={{ color: "var(--accent)" }}>FTR</span> — Fun Time Radio & Drift News
        </h1>
        <div className="flex items-center gap-3">
          <span className="text-xs px-2 py-0.5 rounded-full" style={{
            background: mode === "ai-dj" ? "var(--accent)" : "var(--surface)",
            color: mode === "ai-dj" ? "var(--background)" : "var(--muted)",
            border: mode === "ai-dj" ? "none" : "1px solid var(--border)",
          }}>
            {mode === "ai-dj" ? "AI DJ" : "Jukebox"}
          </span>
          <ListenerCount count={listeners} />
        </div>
      </header>

      <AlbumArt src={np.album_art || null} playing={np.playing} />

      <NowPlaying
        track={np.track}
        artist={np.artist}
        progress_ms={np.progress_ms}
        duration_ms={np.duration_ms}
      />

      <StreamPlayer />

      <SearchBar onQueued={handleQueued} onQueueFailed={handleQueueFailed} />

      <Queue tracks={merged} onRefresh={refreshQueue} />
    </div>
  );
}
